import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Paper, Stack, TextField, Button, Alert, Divider,
  Tooltip, IconButton,
} from '@mui/material';
import { Settings, InfoOutlined } from '@mui/icons-material';
import { gymUpiAPI } from '../services/api';
import PageHeader from '../components/PageHeader';

export default function InvoiceSettings() {
  const currentUser = (() => { try { return JSON.parse(localStorage.getItem('user')); } catch { return null; } })();
  const gymId = currentUser?.gymId?._id || currentUser?.gymId;

  const [settings, setSettings] = useState({ invoiceReminderDays: 3, invoiceNote: '' });
  const [editForm, setEditForm] = useState({ invoiceReminderDays: 3, invoiceNote: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState(null);

  const loadSettings = useCallback(async () => {
    if (!gymId) return;
    setLoading(true);
    try {
      const res = await gymUpiAPI.getSettings(gymId);
      const data = res.data.data || {};
      setSettings(data);
      setEditForm({
        invoiceReminderDays: data.invoiceReminderDays ?? 3,
        invoiceNote: data.invoiceNote ?? '',
      });
    } catch (e) {
      setAlert({ type: 'error', msg: e.response?.data?.message || 'Failed to load settings' });
    } finally {
      setLoading(false);
    }
  }, [gymId]);

  useEffect(() => { loadSettings(); }, [loadSettings]);

  const saveSettings = async () => {
    if (!gymId) {
      setAlert({ type: 'error', msg: 'No gym associated with your account' });
      return;
    }

    setSaving(true);
    setAlert(null);
    try {
      await gymUpiAPI.upsertSettings(gymId, editForm);
      setSettings(editForm);
      setAlert({ type: 'success', msg: 'Invoice settings saved.' });
    } catch (e) {
      setAlert({ type: 'error', msg: e.response?.data?.message || 'Failed to save settings' });
    } finally {
      setSaving(false);
    }
  };

  if (!gymId) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="warning">No gym associated with your account. Invoice settings are gym-scoped.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 960, mx: 'auto' }}>
      <PageHeader
        title="Invoice Settings"
        subtitle="Configure gym payment reminders and invoice notes for members."
        gradient="linear-gradient(90deg, #16A34A, #4ADE80)"
      />

      {alert && (
        <Alert severity={alert.type} sx={{ mb: 3 }} onClose={() => setAlert(null)}>{alert.msg}</Alert>
      )}

      <Paper sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Stack spacing={3}>
          <Box>
            <Typography variant="subtitle2" fontWeight={700} mb={1}>Reminder settings</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              These values are used for invoice reminders and member-facing invoice messaging.
            </Typography>
            <TextField
              label="Invoice reminder days"
              type="number"
              size="small"
              value={editForm.invoiceReminderDays}
              onChange={(e) => setEditForm((f) => ({ ...f, invoiceReminderDays: Number(e.target.value) }))}
              helperText="Send reminders this many days before invoice due date."
              fullWidth
              disabled={loading}
            />
          </Box>

          <Divider />

          <Box>
            <Stack direction="row" alignItems="center" spacing={1} mb={1}>
              <Typography variant="subtitle2" fontWeight={700}>Invoice message</Typography>
              <InfoOutlined sx={{ color: 'text.secondary', fontSize: 16 }} />
            </Stack>
            <TextField
              label="Default invoice note"
              value={editForm.invoiceNote}
              onChange={(e) => setEditForm((f) => ({ ...f, invoiceNote: e.target.value }))}
              placeholder="Enter text to include on invoices and payment reminders"
              multiline
              rows={4}
              fullWidth
              disabled={loading}
            />
          </Box>

          <Divider />

          <Box>
            <Typography variant="subtitle2" fontWeight={700}>Current saved values</Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mt={1}>
              <Paper sx={{ p: 2, flex: 1, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="caption" color="text.secondary">Reminder days</Typography>
                <Typography variant="h6" fontWeight={700}>{settings.invoiceReminderDays ?? '—'}</Typography>
              </Paper>
              <Paper sx={{ p: 2, flex: 2, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="caption" color="text.secondary">Saved invoice note</Typography>
                <Typography variant="body2" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>{settings.invoiceNote || 'No invoice note configured.'}</Typography>
              </Paper>
            </Stack>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button variant="outlined" onClick={loadSettings} disabled={loading || saving}>Reset</Button>
            <Button variant="contained" onClick={saveSettings} disabled={loading || saving}>
              {saving ? 'Saving...' : 'Save settings'}
            </Button>
          </Box>
        </Stack>
      </Paper>
    </Box>
  );
}
